import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
// material
import { styled } from '@mui/material/styles';
import {
  Box,
  Toolbar,
  Tooltip,
  IconButton,
  Typography,
  OutlinedInput,
  InputAdornment,
  Stack,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import jaLocale from 'date-fns/locale/ja';
import { LoadingButton, DesktopDatePicker } from '@mui/lab';
import moment from 'moment';
import { set } from 'lodash';

// ----------------------------------------------------------------------

const RootStyle = styled(Toolbar)(({ theme }) => ({
  height: 96,
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1, 0, 3)
}));

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter
  }),
  '&.Mui-focused': { width: 320, boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`
  }
}));

// ----------------------------------------------------------------------

ListToolbar.propTypes = {
  numSelected: PropTypes.number,
  filterName: PropTypes.string,
  onFilterName: PropTypes.func,
  onDelete: PropTypes.func
};

export default function ListToolbar({
  distributor_code,
  customer_code,
  device_code,
  dtS,
  dtE,
  numSelected,
  filterName,
  onFilterName,
  onDelete,
  onChangeDistributorCode,
  onChangeCustomerCode,
  onChangeDeviceCode,
  onChangeDateS = null,
  onChangeDateE = null
}) {
  const { SystemLib } = window;
  const { userInfo } = SystemLib;
  const [distributorCode, setDistributorCode] = useState(distributor_code);
  const [customerCode, setCustomerCode] = useState(customer_code);
  const [deviceCode, setDeviceCode] = useState(device_code);
  const [distributors, setDistributors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [dto_s, setDtoS] = useState(moment(dtS).toDate());
  const [dto_e, setDtoE] = useState(moment(dtE).toDate());
  const [dt_s, setDtS] = useState(dtS);
  const [dt_e, setDtE] = useState(dtE);

  useEffect(() => {
    getDistributors();
  }, []);

  const getDistributors = async () => {
    const { data } = await SystemLib.api('numberofsales/distributors', {}, 'post');
    if (data.result) {
      setDistributors(data.data);
    }
  };

  const getCustomers = async (distributor_code) => {
    const { data } = await SystemLib.api('numberofsales/customers', { distributor_code }, 'post');
    if (data.result) {
      setCustomers(data.data);
    }
  };

  const getDevices = async (distributor_code, customer_code) => {
    const { data } = await SystemLib.api(
      'numberofsales/devices',
      { distributor_code, customer_code },
      'post'
    );
    if (data.result) {
      setDevices(data.data);
    }
  };

  useEffect(() => {
    getDistributors();
    getCustomers(distributorCode);
    getDevices(distributorCode, customerCode);
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={jaLocale}>
      <RootStyle
        sx={{
          ...(numSelected > 0 && {
            color: 'primary.main',
            bgcolor: 'primary.lighter'
          })
        }}
      >
        {numSelected > 0 ? (
          <Typography component="div" variant="subtitle1">
            {numSelected} 件 選択中
          </Typography>
        ) : (
          <Stack direction="row" spacing={2}>
            {(userInfo.role === 'master' || userInfo.role === 'admin') && (
              <Select
                labelId="select_label_distributor"
                id="select_distributor"
                value={distributorCode}
                onChange={(e) => {
                  setDistributorCode(e.target.value);
                  onChangeDistributorCode(e.target.value);
                  getCustomers(e.target.value);
                  setCustomerCode(-1);
                  setDevices([]);
                  setDeviceCode(-1);
                  onChangeCustomerCode(-1);
                  onChangeDeviceCode(-1);
                }}
              >
                <MenuItem value={-1}>販売代理店選択...</MenuItem>
                {distributors.map((v) => (
                  <MenuItem key={v.code} value={v.code}>
                    {v.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {(userInfo.role === 'master' ||
              userInfo.role === 'admin' ||
              userInfo.role === 'distributor_admin' ||
              userInfo.role === 'distributor_user') && (
              <Select
                labelId="select_label_customer"
                id="select_customer"
                value={customerCode}
                onChange={(e) => {
                  setCustomerCode(e.target.value);
                  onChangeCustomerCode(e.target.value);
                  getDevices(distributorCode, e.target.value);
                  setDevices([]);
                  setDeviceCode(-1);
                  onChangeDeviceCode(-1);
                }}
              >
                <MenuItem value={-1}>設置場所選択...</MenuItem>
                {customers.map((v) => (
                  <MenuItem key={v.code} value={v.code}>
                    {v.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            <Select
              labelId="select_label_device"
              id="select_device"
              value={deviceCode}
              onChange={(e) => {
                setDeviceCode(e.target.value);
                onChangeDeviceCode(e.target.value);
              }}
            >
              <MenuItem value={-1}>デバイス選択...</MenuItem>
              {devices.map((v) => (
                <MenuItem key={v.device_code} value={v.device_code}>
                  {v.with_device.name}
                </MenuItem>
              ))}
            </Select>
            <DesktopDatePicker
              id="dt_s"
              label="開始日"
              inputFormat="yyyy-MM-dd"
              value={dto_s}
              onChange={(v) => {
                setDtoS(moment(v).toDate());
                if (v === '' || v === null) {
                  setDtS(-1);
                  onChangeDateS(-1);
                } else {
                  setDtS(moment(v).format('YYYY-MM-DD'));
                  onChangeDateS(moment(v).format('YYYY-MM-DD'));
                }
              }}
              renderInput={(params) => (
                <TextField disablePast {...params} error={false} helperText={null} />
              )}
            />
            <DesktopDatePicker
              id="dt_e"
              label="終了日"
              inputFormat="yyyy-MM-dd"
              value={dto_e}
              onChange={(v) => {
                setDtoE(moment(v).toDate());
                if (v === '' || v === null) {
                  setDtE(-1);
                  onChangeDateE(-1);
                } else {
                  setDtE(moment(v).format('YYYY-MM-DD'));
                  onChangeDateE(moment(v).format('YYYY-MM-DD'));
                }
              }}
              renderInput={(params) => (
                <TextField disablePast {...params} error={false} helperText={null} />
              )}
            />
            <SearchStyle
              value={filterName}
              onChange={onFilterName}
              placeholder="商品検索..."
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
          </Stack>
        )}
      </RootStyle>
    </LocalizationProvider>
  );
}
